import { computed, nextTick, ref, useTemplateRef, watch, type Ref } from 'vue';
import { useResizeObserver } from '@vueuse/core';
import type { Navigation } from '@/navigation/types';

const LAYOUT_CONSTANTS = {
  ITEM_PADDING_BUFFER: 16, // Account for padding (12px total) + small buffer for measurement precision
  OVERFLOW_BUTTON_SPACE: 44, // The actual button is ~40px + margin
  RESIZE_DEBOUNCE_MS: 50, // Increased debounce for better performance and reliability
  NOT_CALCULATED_YET: -1, // Indicates visible items count not calculated yet
  MIN_CONTAINER_WIDTH: 44, // Minimum width needed for the overflow button
} as const;

export function useNavigationOverflow(navigation: Ref<Navigation | null>) {
  const navigationContainer = useTemplateRef<HTMLDivElement>('navigationContainer');
  const visibleItems = ref<number>(LAYOUT_CONSTANTS.NOT_CALCULATED_YET);
  const showOverflowMenu = ref(false);
  const isCalculating = ref(false);

  const visibleNavigation = computed(() => {
    if (!navigation.value?.entries) return null;

    // If not calculated yet, show all items for measurement
    if (visibleItems.value === LAYOUT_CONSTANTS.NOT_CALCULATED_YET) {
      return navigation.value;
    }

    // Show the calculated number of visible items
    return {
      ...navigation.value,
      entries: navigation.value.entries.slice(0, visibleItems.value),
    };
  });

  const overflowNavigation = computed(() => {
    if (
      !navigation.value?.entries?.length ||
      visibleItems.value >= navigation.value.entries.length
    ) {
      return null;
    }

    return {
      ...navigation.value,
      entries: navigation.value.entries.slice(visibleItems.value),
    };
  });

  const calculateVisibleItems = async () => {
    if (!navigationContainer.value || !navigation.value?.entries || isCalculating.value) {
      return;
    }

    isCalculating.value = true;

    // Reset to measure all items, then calculate what fits
    visibleItems.value = LAYOUT_CONSTANTS.NOT_CALCULATED_YET;

    await nextTick();

    const containerWidth = navigationContainer.value.offsetWidth;

    // Find navigation items using the data attribute selector
    const items = navigationContainer.value.querySelectorAll('[data-nav-item]');

    if (items.length === 0 || containerWidth < LAYOUT_CONSTANTS.MIN_CONTAINER_WIDTH) {
      // If the container is too small, hide all items and use overflow menu
      visibleItems.value =
        containerWidth < LAYOUT_CONSTANTS.MIN_CONTAINER_WIDTH ? 0 : navigation.value.entries.length;
      isCalculating.value = false;
      return;
    }

    let totalWidth = 0;
    let count = 0;

    // Calculate the total width of all items
    const itemWidths: number[] = [];
    for (const item of items) {
      const itemRect = (item as HTMLElement).getBoundingClientRect();
      const itemWidth = itemRect.width + LAYOUT_CONSTANTS.ITEM_PADDING_BUFFER;
      itemWidths.push(itemWidth);
      totalWidth += itemWidth;
    }

    // Reserve space for the overflow button
    const availableWidth = containerWidth - LAYOUT_CONSTANTS.OVERFLOW_BUTTON_SPACE;

    // Check if all items fit without overflow
    if (totalWidth <= containerWidth) {
      visibleItems.value = navigation.value.entries.length;
      isCalculating.value = false;
      return;
    }

    // Calculate how many items fit
    let usedWidth = 0;
    for (const width of itemWidths) {
      if (usedWidth + width <= availableWidth) {
        usedWidth += width;
        count++;
      } else {
        break;
      }
    }

    // Allow zero visible items if space is too constrained
    // The overflow menu will handle showing all items
    visibleItems.value = count;
    isCalculating.value = false;
  };

  // Use VueUse's useResizeObserver for better reliability
  let resizeTimeoutId: ReturnType<typeof setTimeout> | null = null;

  useResizeObserver(navigationContainer, () => {
    // Clear the previous timeout to debounce
    if (resizeTimeoutId) {
      clearTimeout(resizeTimeoutId);
    }

    resizeTimeoutId = setTimeout(async () => {
      await calculateVisibleItems();
      resizeTimeoutId = null;
    }, LAYOUT_CONSTANTS.RESIZE_DEBOUNCE_MS);
  });

  // Initialize calculation when navigation becomes available
  watch(
    [navigation, navigationContainer],
    async () => {
      if (navigation.value?.entries && navigationContainer.value) {
        await nextTick();
        await calculateVisibleItems();
      }
    },
    { immediate: true },
  );

  return {
    navigationContainer,
    visibleItems,
    showOverflowMenu,
    visibleNavigation,
    overflowNavigation,
  };
}
